/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Locale } from "@i18n/next";
import { useLocale, usePathname, useTranslations } from "@i18n/next";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  Creation,
  isValidKidsLocale,
  MAX_RELATED_ACCOUNTS_IS_KID,
  ProfilesSwitch,
  useIsKidsProfilesVisible,
  useKidProfiles,
  useRequestMePinExists,
  useRequestMeRelatedAccounts,
} from "@packages/kids";
import {
  isFamilyPlan,
  isFree,
  isMobileSubscription,
  useIsBlockedFromManagingProfiles,
  useIsTeacher,
  useRequestGiftHistory,
  useRequestMe,
  useRequestSubscriptions,
  useSession,
} from "@packages/sdk";
import {
  AccountIcon,
  ActivityIcon,
  Avatar,
  BellIcon,
  BlackboardIcon,
  Button,
  cssMediaQueryMinWidthTablet,
  EmailIcon,
  FriendsIcon,
  GiftIcon,
  LanguageIcon,
  LegalIcon,
  LockClosedIcon,
  MembersIcon,
  MergeIcon,
  PasswordIcon,
  PaymentIcon,
  PhoneIcon,
  PlusIcon,
  ProfileIcon,
  QueueIcon,
  SettingsIcon,
  Sidebar,
  SidebarTab,
  SortIcon,
  spacing,
  StreakIcon,
  SubscriptionIcon,
  SupportIcon,
  Text,
  type ThemeType,
  TrashIcon,
  useModal,
} from "@packages/ui";

const buttonCss: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: "transparent !important",
  color: theme.semanticColors.neutralsMedium,
  gap: spacing.ms,
  justifyContent: "flex-start",
  paddingLeft: spacing.s,
  paddingRight: spacing.s,

  ":hover": {
    backgroundColor: "transparent !important",
    color: theme.semanticColors.primary,
  },
  ":active": {
    backgroundColor: "transparent !important",
    color: theme.semanticColors.primary,
  },
  ":focus-visible": { backgroundColor: "transparent !important" },
});

const kidButtonCss: Interpolation<ThemeType> = {
  paddingLeft: spacing.s,
  paddingRight: spacing.s,
};

const titleCss: Interpolation = {
  padding: `${spacing.s} ${spacing.ms}`,
};

const baseCss: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "flex" },
};

const mobileRootNavigationCss: Interpolation = {
  display: "flex",
};

const sidebarBorderCss: Interpolation = {
  display: "none",
  [cssMediaQueryMinWidthTablet]: { display: "block" },
};

export const SettingsSidebar = () => {
  const t = useTranslations();
  const { status } = useSession();
  const { query: user } = useRequestMe();
  const { query: subscriptions } = useRequestSubscriptions();
  const { query: giftHistory } = useRequestGiftHistory();
  const { data: relatedAccounts } = useRequestMeRelatedAccounts();
  const hasKidsProfiles = relatedAccounts?.some((a) => a.is_kid) ?? false;
  const { data: pinExists } = useRequestMePinExists({
    enabled: !user.data?.is_kid,
  });
  const pathname = usePathname();
  const isRoot = /^\/(?:.+\/)?settings\/?$/.test(pathname);
  const isTeacher = useIsTeacher();
  const kidProfiles = useKidProfiles() ?? [];
  const isBlockedFromManagingProfiles = useIsBlockedFromManagingProfiles();
  const creationModal = useModal(Creation);
  const { track } = useAnalytics();
  const isKidsProfilesVisible = useIsKidsProfilesVisible();
  const locale = useLocale();
  const { flags } = useMixpanel();
  const isNotificationsEnabled =
    flags?.[HallowABFlag.WebNotifications]?.isTargeted ?? false;

  const isLoadingUser =
    status === "loading" || (status === "authenticated" && !user.data);

  if (isLoadingUser) return null;

  if (user.data?.is_kid) {
    return (
      <div css={[baseCss, isRoot && mobileRootNavigationCss]}>
        <Sidebar borderCSS={sidebarBorderCss} state="expanded">
          <Text size="xl" type="title" css={titleCss} as="h2">
            {t("settings_title")}
          </Text>
          <SidebarTab
            href="/settings/legal"
            icon={<LegalIcon />}
            message="settings_legal"
          />
          <Button asChild variant="transparentNeutral" css={buttonCss}>
            <ProfilesSwitch
              switchProfilesProps={{
                onSuccess: () => window.location.assign("/home"),
              }}
            >
              <SortIcon size={24} rotate={90} />
            </ProfilesSwitch>
          </Button>
        </Sidebar>
      </div>
    );
  }

  return (
    <div css={[baseCss, isRoot && mobileRootNavigationCss]}>
      <Sidebar borderCSS={sidebarBorderCss} state="expanded">
        <Text size="xl" type="title" css={titleCss} as="h2">
          {t("settings_title")}
        </Text>
        {!!user.data && (
          <SidebarTab
            href="/settings/account"
            icon={<AccountIcon />}
            message="settings_account"
          >
            <SidebarTab
              href="/settings/account/profile"
              icon={<ProfileIcon />}
              message="settings_update_profile"
            />
            <SidebarTab
              href="/settings/account/email"
              icon={<EmailIcon />}
              message="general_email"
            />
            <SidebarTab
              href="/settings/account/phone"
              icon={<PhoneIcon />}
              message="general_phone_number"
            />
            <SidebarTab
              href="/settings/account/streaks"
              icon={<StreakIcon.Outline />}
              message="settings_streaks"
            />
            {user.data.email && (
              <SidebarTab
                href="/settings/account/password"
                icon={<PasswordIcon />}
                message={
                  user.data.has_password
                    ? "change_password_title"
                    : "settings_add_password"
                }
              />
            )}
            <SidebarTab
              href="/settings/account/merge"
              icon={<MergeIcon />}
              message="account_merge_title"
            />
            {!user.data?.is_kid && hasKidsProfiles && pinExists?.exists && (
              <SidebarTab
                analyticsEvent={{
                  event: HallowAnalyticsEvent.TappedChangeParentPINSettings,
                  properties: { screen_name: "settings" },
                }}
                href="/settings/account/pin"
                icon={<LockClosedIcon.Outline />}
                message="settings_parent_pin"
              />
            )}
            <SidebarTab
              href="/settings/account/delete"
              icon={<TrashIcon />}
              message="settings_delete_account_delete"
            />
          </SidebarTab>
        )}
        {!!user.data && isNotificationsEnabled && (
          <SidebarTab
            href="/settings/notifications"
            icon={<BellIcon.On />}
            message="settings_notifications_title"
          />
        )}
        {!!user.data && (
          <SidebarTab
            href="/settings/subscription"
            icon={<SubscriptionIcon />}
            message="settings_subscription"
          />
        )}
        {!!user.data &&
          !!subscriptions.data?.length &&
          isFamilyPlan("subscription", subscriptions.data[0]) && (
            <SidebarTab
              icon={<FriendsIcon />}
              message="product_name_family"
              href="/settings/family"
            />
          )}
        {!user.data?.is_kid && isKidsProfilesVisible && (
          <SidebarTab
            icon={<MembersIcon />}
            message="settings_manage_kids"
            href="/settings/kids"
          >
            {kidProfiles.map((kid) => (
              <SidebarTab
                key={kid.id}
                href={`/settings/kids/${kid.id}`}
                message={kid.name}
                leftSideContent={<Avatar imgSrc={kid.image_url} size={24} />}
                buttonCss={kidButtonCss}
              />
            ))}
            {isValidKidsLocale(locale as Locale) &&
              !isBlockedFromManagingProfiles &&
              kidProfiles.length < MAX_RELATED_ACCOUNTS_IS_KID && (
                <Button
                  variant="transparentNeutral"
                  css={buttonCss}
                  startIcon={<PlusIcon.Simple />}
                  onClick={() => {
                    track({
                      event: HallowAnalyticsEvent.TappedAddKidsProfile,
                      properties: { screen_name: "settings" },
                    });
                    creationModal.show({});
                  }}
                >
                  {t("settings_add_kids_profile")}
                </Button>
              )}
          </SidebarTab>
        )}
        {((!!subscriptions.data?.length &&
          !isFree(subscriptions.data[0]) &&
          !isMobileSubscription(subscriptions.data[0])) ||
          !!giftHistory.data?.gifts?.length) && (
          <SidebarTab
            href="/settings/payment"
            icon={<PaymentIcon />}
            message="user_payment_title"
          />
        )}
        {user.data?.is_herald ? (
          <SidebarTab
            href={"/settings/referrals"}
            icon={<GiftIcon.Outline />}
            message={"herald_dashboard_leaderboard_referrals"}
          >
            <SidebarTab
              href={"/settings/referrals/dashboard"}
              icon={<ActivityIcon />}
              message={"web_dashboard"}
            />
            <SidebarTab
              href={"/settings/referrals/leaderboards"}
              icon={<QueueIcon />}
              message={"herald_dashboard_leaderboards_tab"}
            />
            <SidebarTab
              href={"/settings/referrals/prizes"}
              icon={<GiftIcon.Outline />}
              message={"herald_dashboard_prizes_tab"}
            />
          </SidebarTab>
        ) : null}
        {isTeacher && (
          <SidebarTab
            href={"https://teachers.hallow.com"}
            icon={<BlackboardIcon />}
            message={"web_teacher_resources_link"}
            target={"_blank"}
          />
        )}
        <SidebarTab
          href="/settings/language"
          icon={<LanguageIcon />}
          message="settings_language_title"
        />
        <SidebarTab
          href="https://help.hallow.com"
          icon={<SupportIcon />}
          message="settings_support"
          target={"_blank"}
        />
        <SidebarTab
          href="/settings/legal"
          icon={<LegalIcon />}
          message="settings_legal"
        />
        <SidebarTab
          href={"/settings/app"}
          icon={<SettingsIcon />}
          message={"web_app_settings_title"}
        />
      </Sidebar>
    </div>
  );
};
